// "How the year was used" — the district's dosage mix and the per-school weekly
// grid, in ONE card, sharing one time axis.
//
// One card, not two stacked sections: they are two views of a single quantity
// (lessons delivered) at two granularities, and read apart they invite the two
// classic misreadings — a busy ribbon that is really three teachers, or a
// healthy-looking mix in a district where only two schools ever started. Together
// the reader gets both halves of "who, when, and how much" in one glance:
//
//   upper   monthly stacked bars — how much ABii each learner actually got
//   axis    drawn once, BETWEEN them, so it reads as the axis of either plot
//   lower   one row per school, one cell per week — where and when it happened
//
// Each panel's title sits on the far side of its own plot from the axis (upper
// panel above, lower panel below), which is what leaves the middle free for the
// one thing both plots have to be read against.
//
// The two panels are pinned to the same gutters (the ribbon's school-name column
// on the left, its totals column on the right), so a date lands at the same x in
// both: a dark stretch in a school's row sits directly under the bars it produced.
// The grains deliberately differ — months read the mix, weeks read a push, and a
// focus cycle is 3–12 weeks, which monthly cells cannot show the shape of.
//
// Deliberately NOT here: a per-bucket "share of roster" strip under the mix.
// Enrollment barely moves across a year, so share-of-roster is the mix's own bar
// heights with the y squashed — the same plot twice. The denominator rides as one
// number in the caption instead, which is the part a reader genuinely can't get
// off the bars.
import React, { useState } from "react";
import FA from "../../fa";
import { MUTED } from "../tokens";
import { Card, Legend, SectionHead, PanelHead, PlotNote } from "../ui";
import {
  AdoptionRibbon,
  RIBBON_GUTTER_LEFT,
  RIBBON_GUTTER_RIGHT,
} from "./AdoptionRibbon";
import {
  MixChart,
  MixControls,
  MixNoteDetails,
  mixLegend,
  mixReach,
} from "./EngagementTab";

// The card's own padding, and the chart-area gutters that follow from it. The
// ribbon's exported offsets are measured from OUTSIDE its card, so the chart
// inside this one subtracts the border and this padding.
const PANEL_PAD = 14;
const GUTTER = {
  left: RIBBON_GUTTER_LEFT - 1 - PANEL_PAD,
  right: RIBBON_GUTTER_RIGHT - 1 - PANEL_PAD,
};
const MIX_H = 190;

const YearUsagePanel = ({
  api,
  org,
  windowSpec,
  comp,
  grain,
  onGrainChange,
  loading,
  failed,
  cycleBands,
  cycleLabel,
}) => {
  const [show, setShow] = useState("engaged");
  const engagedOnly = show === "engaged";
  const ready = !!(comp && comp.tiers && comp.composition_by_bucket);
  const {
    tiers = [],
    interval,
    composition_by_bucket: rows = [],
  } = comp || {};
  const period = interval || "period";
  const reach = ready ? mixReach(rows, tiers) : null;
  return (
    <div style={{ margin: "0 0 24px" }}>
      <SectionHead
        title="How the year was used"
        note="the bars say how much · the grid says who and when"
      />
      <Card pad={PANEL_PAD}>
        <PanelHead
          title="Lessons per learner"
          sub={
            <>
              One bar per {period} — height is learners with lessons, colour is
              how much they used.
              {/* The denominator, as a scalar. Peak rather than mean: the
                  question this answers is "how big did the engaged group ever
                  get against the roster", and an average across a year that
                  includes summer understates it. */}
              {reach && reach.roster
                ? ` Busiest ${period}: ${reach.peak.toLocaleString()} of ${reach.roster.toLocaleString()} enrolled took a lesson.`
                : ""}
            </>
          }
          controls={
            <MixControls
              grain={grain}
              onGrainChange={onGrainChange}
              interval={interval}
              show={show}
              setShow={setShow}
            />
          }
        />
        {failed && (
          <div style={{ fontSize: 13, color: MUTED, padding: "8px 0" }}>
            Couldn’t load the engagement mix for this period.
          </div>
        )}
        {ready && (
          <>
            <Legend items={mixLegend(tiers, engagedOnly)} />
            <div style={{ height: MIX_H }}>
              <MixChart
                comp={comp}
                engagedOnly={engagedOnly}
                cycleBands={cycleBands}
                gutter={GUTTER}
                // the shared axis is drawn once, by the ribbon below
                showXLabels={false}
              />
            </div>
          </>
        )}
        {!ready && loading && (
          <div style={{ fontSize: 13, color: MUTED, padding: "8px 0" }}>
            <FA color="gray" icon="spinner" spin /> Rebuilding the mix…
          </div>
        )}
        {/* No divider between the two panels: the shared week axis now sits
            between them (drawn by the ribbon in `bare` mode, above its rows) and
            is the thing that joins them. A hairline there would cut the card in
            half again, and it would put a line between each plot and the axis
            that both are read against. */}
        <div style={{ height: 10 }} />
        <AdoptionRibbon api={api} org={org} windowSpec={windowSpec} bare />
      </Card>
      <PlotNote
        style={{ margin: "10px 0 0" }}
        details={
          <>
            Cell darkness in the grid is scaled to the district's busiest
            school-week, so rows are comparable with each other rather than each
            normalized to itself. Outlined stretches are focus cycles detected per
            school from its own usage — schools rarely push at the same time,
            which is why a district-wide average can report “evenly spread” while
            every school in it ran a real push. Neutral density is volume, the
            tier palette is usage; both panels cover the same weeks, so a dark
            stretch in a row sits under the bars it produced.{" "}
            <MixNoteDetails
              comp={comp}
              engagedOnly={engagedOnly}
              interval={interval}
              cycleBands={cycleBands}
              cycleLabel={cycleLabel}
            />
          </>
        }
      >
        Lessons per learner by usage tier above, lessons per school per week
        below — same window, same weeks, one axis between them.
        {comp &&
        comp.buckets &&
        comp.buckets.length &&
        comp.buckets[comp.buckets.length - 1].partial ? (
          <>
            {" "}
            The faded final column is a period still in progress — a partial one
            puts learners in low tiers simply because little of it has happened
            yet.
          </>
        ) : null}
      </PlotNote>
    </div>
  );
};

export { YearUsagePanel };
